/**
 * Bounded, lossless undo/redo command history for Paper2Ink.
 * Guarantees zero base64 source image duplication.
 */

import type { CIMDocument } from './model.ts';
import type { Command } from './commands.ts';

export class CommandHistory {
  private readonly maxCapacity: number;
  private undoStack: Command[] = [];
  private redoStack: Command[] = [];

  constructor(maxCapacity: number = 100) {
    if (maxCapacity < 1) {
      throw new Error(`History capacity must be at least 1, got ${maxCapacity}`);
    }
    this.maxCapacity = maxCapacity;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  get undoCount(): number {
    return this.undoStack.length;
  }

  get redoCount(): number {
    return this.redoStack.length;
  }

  execute(command: Command, currentDoc: CIMDocument): CIMDocument {
    const newDoc = command.execute(currentDoc);
    this.undoStack.push(command);
    if (this.undoStack.length > this.maxCapacity) {
      this.undoStack.shift();
    }
    this.redoStack = [];
    return newDoc;
  }

  undo(currentDoc: CIMDocument): CIMDocument {
    if (!this.canUndo) {
      return currentDoc;
    }
    const cmd = this.undoStack.pop()!;
    const undoneDoc = cmd.undo(currentDoc);
    this.redoStack.push(cmd);
    return undoneDoc;
  }

  redo(currentDoc: CIMDocument): CIMDocument {
    if (!this.canRedo) {
      return currentDoc;
    }
    const cmd = this.redoStack.pop()!;
    const redoneDoc = cmd.execute(currentDoc);
    this.undoStack.push(cmd);
    return redoneDoc;
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
  }
}
